import { motion } from 'framer-motion';
import { Bot, MessageCircle, Languages, PenTool, Wrench, BookOpen, Sparkles } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import AITutor from '../3d/AITutor';

const features = [
  { key: 'ai.feature1', icon: Bot, color: 'from-blue-500 to-cyan-500' },
  { key: 'ai.feature2', icon: BookOpen, color: 'from-green-500 to-emerald-500' },
  { key: 'ai.feature3', icon: Languages, color: 'from-purple-500 to-violet-500' },
  { key: 'ai.feature4', icon: PenTool, color: 'from-pink-500 to-rose-500' },
  { key: 'ai.feature5', icon: Wrench, color: 'from-orange-500 to-amber-500' },
  { key: 'ai.feature6', icon: MessageCircle, color: 'from-cp-orange to-cp-orange-light' },
];

export default function MSCAI() {
  const { t } = useLanguage();

  return (
    <section id="ai" className="section-padding bg-gradient-to-b from-cp-blue-dark to-cp-blue relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-32 w-96 h-96 bg-cp-orange/20 rounded-full blur-3xl" />
      </div>

      <div className="container-custom relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* 3D Scene */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-2 lg:order-1"
          >
            <AITutor />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="order-1 lg:order-2"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-500/30 mb-6"
            >
              <Sparkles className="w-4 h-4 text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">AI Powered</span>
            </motion.div>

            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
              {t('ai.title')}
            </h2>
            <p className="text-xl text-cp-orange font-semibold mb-6">
              {t('ai.subtitle')}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <motion.div
                    key={feature.key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="glass rounded-xl p-4 hover:bg-white/5 transition-colors group"
                  >
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${feature.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <h3 className="text-white font-semibold mb-1">{t(feature.key)}</h3>
                    <p className="text-sm text-gray-400">{t(`${feature.key}.desc`)}</p>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="mt-8"
            >
              <a
                href="#contact"
                className="btn-primary inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-white"
              >
                {t('contact.enquiry')}
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}